import { Component, OnInit, Output, EventEmitter } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { KpiService } from "../../kpi/kpi.service";
import { OOPS_MESSAGES, STAND_CUSTOMER_ID } from "../../constants";

import * as _ from "lodash";
import { student } from "../../store/students/state";
import { map } from "rxjs/operators";

@Component({
  selector: "student-info-panel",
  templateUrl: "./student-info-panel.component.html",
  styleUrls: ["./student-info-panel.component.scss"],
})
export class StudentInfoPanelComponent implements OnInit {
  @Output() newStudentData = new EventEmitter<boolean>();
  @Output() newSchoolEvent = new EventEmitter();
  studentData;
  expandedView = false;
  school;
  studentsDropdown;
  studentId;
  errorMessage;
  successMessage;
  initialStudent;
  initialSchool;
  schoolId;
  schoolsDropdown;
  userInfo;
  allSchoolsWithStudentObjects = [];
  studentsDropdownDictionary = [];
  imageBlobUrl;

  constructor(
    public kpiService: KpiService,
    private route: ActivatedRoute,
    public router: Router
  ) {}

  ngOnInit() {
    // we get the user info to get a list of schools. We'll use the first school to populate the
    // dropdown initially
    this.kpiService.getUserInfo().subscribe(
      (data) => {
        this.userInfo = data;
        if (this.userInfo.schools.length > 0) {
          // only populate the dropdown if the user has schools. If the user has no schools,
          // they have no students
          this.populateSchoolsDropdown();
        }
        this.subscribeToRouteParams();
        this.hideMessages();
      },
      (error) => {
        this.showErrorMessage(
          "<b>Heads up!</b> We were unable to get the user info."
        );
      }
    );
  }

  populateSchoolsDropdown() {
    this.schoolsDropdown = _.map(this.userInfo.schools, function (school: any) {
      return {
        id: school.systemSchoolId,
        text: school.officialSchoolName,
      };
    });
    this.schoolsDropdown = _.sortBy(this.schoolsDropdown, "text");
    this.initialSchool = {
      id: this.schoolsDropdown[0].id,
      text: this.schoolsDropdown[0].text,
    };
  }

  toggleExpandedView(expanded) {
    this.expandedView = expanded;
  }

  getDemographicKeys() {
    return Object.keys(this.studentData.demographics);
  }

  changeStudent(event) {
    this.kpiService.selectedStudentIdInStudentProfile = event.id;
    this.router.navigate(["/profile/student", event.id]);
  }

  changeSchool(event) {
    this.studentsDropdownDictionary = [];
    this.getSchoolInfo(event.id, null);
  }

  getStudentInfo(studentId) {
    this.imageBlobUrl = null;
    this.kpiService.getStudentInfo(studentId).subscribe(
      (data) => {
        this.studentData = data;
        this.newStudentData.emit(this.studentData);
        if (
          !this.allSchoolsWithStudentObjects[data.systemSchoolId] ||
          (this.school && data.systemSchoolId !== this.school.systemSchoolId)
        ) {
          this.getSchoolInfo(data.systemSchoolId, data);
        } else {
          this.createInitialStudent(data);
        }
        this.hideMessages();
      },
      (error) => {
        this.studentData = [];
        this.newStudentData.emit(this.studentData);
        this.initialStudent = { id: "", text: "" };
        this.showErrorMessage(
          "<b>Heads up!</b> We were unable to get the data for this student."
        );
      }
    );
    this.kpiService.getStudentPhoto(studentId).subscribe(
      (data) => {
        this.createImageFromBlob(data);
      },
      (error) => {
        console.log(error);
      }
    );
  }

  createImageFromBlob(image: Blob) {
    let reader = new FileReader();
    reader.addEventListener(
      "load",
      () => {
        this.imageBlobUrl = reader.result;
      },
      false
    );
    if (image) {
      reader.readAsDataURL(image);
    }
  }

  subscribeToRouteParams() {
    this.route.params.subscribe((params) => {
      this.studentId = params["studentId"];
      // if we get params in the url, then get the student info
      if (this.studentId) {
        this.getStudentInfo(this.studentId);
        this.hideMessages();
      } else if (this.kpiService.selectedStudentIdInStudentProfile) {
        // we have a selected student id in the kpi service (using as store)
        this.studentId = this.kpiService.selectedStudentIdInStudentProfile;
        this.getStudentInfo(this.studentId);
        window.history.replaceState(
          {},
          "",
          `/profile/student/${this.studentId}`
        );
      } else if (this.schoolsDropdown && this.schoolsDropdown[0]) {
        // otherwise, use the first school in the school dropdown to get the school info
        // and then get the first student from there
        this.getSchoolInfo(this.schoolsDropdown[0].id, null);
        this.hideMessages();
      } else {
        this.kpiService.showError(
          null,
          OOPS_MESSAGES.RENDER_VIEW,
          OOPS_MESSAGES.RENDER_VIEW
        );
      }
    });
  }

  getSchoolInfo(id, studentData) {
    if (this.userInfo.role == "teacher") {
      //For teachers, accessible students are stored by school inside userInfo.schools
      this.school = _.find(this.userInfo.schools, ["systemSchoolId", id]);
      this.newSchoolEvent.emit(this.school);
      this.allSchoolsWithStudentObjects[id] = this.school;
      this.getSchoolStudentInfo(id, studentData);
    } else {
      //For non-teachers, get the complete student list from /schoolinfo
      this.getSchoolDetails(id).subscribe(
        (data) => {
          this.getSchoolStudentInfo(id, studentData);
        },
        (err) => console.error(err)
      );
    }
  }

  getSchoolDetails(schoolId) {
    return this.kpiService.getSchoolInfo(schoolId).pipe(
      map(
        (data) => {
          this.school = data;
          this.newSchoolEvent.emit(this.school);
          this.allSchoolsWithStudentObjects[schoolId] = this.school;
        },
        (error) => {
          this.kpiService.showError(
            error,
            OOPS_MESSAGES.ACCESS_CONTENT,
            OOPS_MESSAGES.RENDER_DASHBOARD
          );
        }
      )
    );
  }

  getSchoolStudentInfo(id, studentData: student) {
    if (this.school.students && this.school.students.length > 0) {
      this.createStudentsDropdown();
      if (!studentData) {
        // if we already have the student from the route params, then we're just getting
        // the school info to populate the dropdown so avoid making a duplicate call
        this.kpiService.selectedStudentIdInStudentProfile = this.studentsDropdown[0].id;
        this.router.navigate(["/profile/student", this.studentsDropdown[0].id]);
      } else {
        this.initialSchool = {
          id: this.school.systemSchoolId,
          text: this.school.officialSchoolName,
        };
        this.kpiService.selectedStudentIdInStudentProfile =
          studentData.studentKEY;
        this.createInitialStudent(studentData);
      }
      this.hideMessages();
    } else {
      // this school has no students
      this.kpiService.selectedStudentIdInStudentProfile = null;
      this.router.navigate(["/profile/student", false]);
      this.studentData = [];
      this.studentsDropdown = [];
      this.newStudentData.emit(this.studentData);
      this.initialStudent = { id: "", text: "" };
      this.showErrorMessage(
        "<b>Heads up!</b> This school has no students. Please select another school."
      );
    }
  }

  createStudentsDropdown() {
    let studentsDropdown = _.map(this.school.students, (student: any) => {
      let studentName = this.createStudentName(student);
      let studentId = student.studentKEY.toString();
      if (this.studentsDropdownDictionary[studentName]) {
        // we've seen this student name before
        if (!this.studentsDropdownDictionary[studentName][studentId]) {
          // this isn't a duplicate student because the id is different. We're seeing a new student with the same name
          this.studentsDropdownDictionary[studentName][studentId] =
            studentName +
            "_" +
            (Object.keys(this.studentsDropdownDictionary[studentName]).length +
              1);
          return {
            id: student.studentKEY,
            text: this.studentsDropdownDictionary[studentName][studentId],
          };
        }
      } else {
        // we haven't seen this student name before
        this.studentsDropdownDictionary[studentName] = [];
        this.studentsDropdownDictionary[studentName][studentId] = studentName;
        return {
          id: student.studentKEY,
          text: studentName,
        };
      }
    });
    studentsDropdown = _.filter(studentsDropdown, function (student) {
      return !_.isEmpty(student);
    });
    this.studentsDropdown = _.sortBy(studentsDropdown, "text");
  }

  createStudentName(student) {
    let studentName;
    if (
      (student.lastName === "" && student.firstName === "") ||
      (student.lastName === "-----" && student.firstName === "-----")
    ) {
      studentName = "(name not provided)";
    } else if (student.lastName === "-----") {
      studentName = student.firstName;
    } else if (student.firstName === "-----") {
      studentName = student.lastName;
    } else {
      studentName = student.lastName + ", " + student.firstName;
    }
    return studentName;
  }

  createInitialStudent(data) {
    let studentName = this.createStudentName(data);
    let studentId = data.studentKEY.toString();
    if (
      this.studentsDropdownDictionary &&
      this.studentsDropdownDictionary[studentName] &&
      this.studentsDropdownDictionary[studentName][studentId]
    ) {
      this.initialStudent = {
        id: data.studentKEY,
        text: this.studentsDropdownDictionary[studentName][studentId],
      };
    } else {
      this.initialStudent = { id: data.studentKEY, text: studentName };
    }
  }

  demographicToShow(demographic) {
    let dem = demographic.toLowerCase();
    if (
      dem !== "gender" &&
      dem !== "grade level" &&
      dem !== "current grade level"
    ) {
      return true;
    } else {
      return false;
    }
  }

  hideMessages() {
    this.successMessage = null;
    this.errorMessage = null;
  }

  showSuccessMessage(message) {
    this.successMessage = message;
    this.errorMessage = null;
  }

  showErrorMessage(message) {
    this.errorMessage = message;
    this.successMessage = null;
  }

  isDemoSite() {
    return (
      this.userInfo.organizationId == "9999" ||
      this.userInfo.organizationId == STAND_CUSTOMER_ID
    );
  }
}
